import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import { AppBar, Toolbar, Tabs, Tab, Box, MenuItem, Menu, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { trackCta } from '../../../Utils/analytics';
import { ASSESSMENT_BUSINESS_PATH, ASSESSMENT_CANDIDATE_PATH } from '../../../Utils/urls';

const StyledTabs = styled(Tabs)({
	width: 'fit-content',
	maxWidth: '100%',
	'.MuiTabs-flexContainer': {
		gap: '50px', // Initial gap
		'@media (max-width: 1052px)': {
			gap: '30px' // Reduced gap for smaller screens
		},
		'@media (max-width: 600px)': {
			gap: '15px' // Further reduced for mobile screens
		}
	},
	'& .MuiTabs-scrollButtons': {
		zIndex: 10,
		'&.Mui-disabled': {
			opacity: 0.3
		}
	},
	// Center tabs on large screens when they fit
	'@media (min-width: 1053px)': {
		'& .MuiTabs-flexContainer': {
			justifyContent: 'center'
		},
		'& .MuiTabs-scroller.MuiTabs-scrollableX': {
			justifyContent: 'flex-start'
		},
		'& .MuiTabs-scroller.MuiTabs-scrollableX .MuiTabs-flexContainer': {
			justifyContent: 'flex-start'
		}
	}
});

const StyledTab = styled(Tab)(({ theme }) => ({
	textTransform: 'none',
	fontSize: '16px',
	fontWeight: 400,
	fontFamily: 'DM Mono',
	lineHeight: '150%',
	letterSpacing: '-0.32px',
	minWidth: 'unset', // Ensures width fits content
	padding: '6px 12px',
	color: '#000',
	'&.Mui-selected': {
		borderBottom: `2px solid ${theme.palette.primary.main}`
	},
	'@media (max-width: 1024px)': {
		fontSize: '12px' // Further reduced for mobile screens
	}
}));
const CustomMenu = styled(Menu)(() => ({
	pointerEvents: 'none',
	'& .MuiPaper-root': {
		pointerEvents: 'auto',
		border: '1px solid #E6E8EC',
		borderRadius: '16px',
		marginTop: '10px',
		padding: '10px',
		minWidth: 320,
		boxShadow: '0 12px 40px -12px rgba(15, 27, 45, 0.14)',
		overflow: 'visible'
	},
	'& .MuiMenu-list': {
		padding: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: '2px'
	}
}));

const CustomMenuItem = styled(MenuItem)(() => ({
	display: 'flex',
	alignItems: 'center',
	gap: '13px',
	padding: '11px 12px',
	borderRadius: '11px',
	fontFamily: 'DM Sans',
	whiteSpace: 'normal',
	backgroundColor: '#FFFFFF',
	transition: 'background-color 0.15s',
	'&.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible': {
		backgroundColor: '#EEF3FE'
	},
	'&:hover': {
		backgroundColor: '#EEF3FE'
	}
})) as typeof MenuItem;

const TopNavMenu = () => {
	const [selectedTab, setSelectedTab] = useState(0);
	const navigate = useNavigate();
	const [productAnchorEl, setProductAnchorEl] = useState<null | HTMLElement>(null);
	const tabsRef = useRef<HTMLDivElement | null>(null);
	const productCloseTimerRef = useRef<number | null>(null);
	const clearProductCloseTimer = () => {
		if (productCloseTimerRef.current) {
			window.clearTimeout(productCloseTimerRef.current);
			productCloseTimerRef.current = null;
		}
	};
	const closeProductMenu = () => {
		clearProductCloseTimer();
		setProductAnchorEl(null);
	};
	const queueProductMenuClose = () => {
		clearProductCloseTimer();
		productCloseTimerRef.current = window.setTimeout(() => {
			setProductAnchorEl(null);
			productCloseTimerRef.current = null;
		}, 350);
	};
	const openProductMenu = (event: React.MouseEvent<HTMLElement>) => {
		clearProductCloseTimer();
		setProductAnchorEl(event.currentTarget);
	};

	useEffect(() => clearProductCloseTimer, []);

	// console.log(selectedTab, 'selectedTab');
	const { pathname } = useLocation();

	useEffect(() => {
		if (pathname === '/product' || pathname === '/product/6d-assessment' || pathname.startsWith('/assessment-service')) {
			setSelectedTab(1);
		}
		if (pathname === '/about-us') {
			setSelectedTab(4);
		}
		if (pathname === '/trust-center') {
			setSelectedTab(7);
		}

		setProductAnchorEl(null);
	}, [pathname]);

	useEffect(() => {
		if (!tabsRef.current) return;

		if (pathname.startsWith('/product')) {
			const scroller = tabsRef.current.querySelector('.MuiTabs-scroller') as HTMLDivElement | null;
			if (scroller) {
				scroller.scrollLeft = 0;
			}
		}
	}, [pathname]);

	useEffect(() => {
		if (!productAnchorEl) return;

		const startY = window.scrollY;
		let rafId: number | null = null;

		const handleScroll = () => {
			if (Math.abs(window.scrollY - startY) < 8) return;

			if (rafId) return;

			rafId = requestAnimationFrame(() => {
				setProductAnchorEl(null);
				rafId = null;
			});
		};

		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			if (rafId) cancelAnimationFrame(rafId);
			window.removeEventListener('scroll', handleScroll);
		};
	}, [productAnchorEl]);

	const ddIconSx = {
		width: 36,
		height: 36,
		borderRadius: '9px',
		backgroundColor: '#EEF3FE',
		color: '#0073DE',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0
	};

	const ddTitleSx = {
		fontFamily: 'DM Sans',
		fontWeight: 600,
		fontSize: '14.5px',
		lineHeight: 1.3,
		color: '#292929'
	};

	return (
		<Box sx={{ width: '100%', overflow: 'hidden' }}>
			<AppBar
				position="static"
				color="transparent"
				elevation={0} // Removes shadow
				sx={{ boxShadow: 'none', backgroundColor: 'transparent', width: '100%' }} // Ensures no border/shadow
			>
				<Toolbar
					sx={{
						padding: '0 !important',
						minHeight: 'auto !important',
						overflow: 'hidden',
						width: '100%',
						justifyContent: 'center',
						'@media (max-width: 1052px)': {
							justifyContent: 'flex-start'
						}
					}}
				>
					<CustomMenu
						id="product-menu"
						anchorEl={productAnchorEl}
						open={Boolean(productAnchorEl)}
						onClose={closeProductMenu}
						hideBackdrop
						disableScrollLock
						disableAutoFocusItem
						anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
						transformOrigin={{ vertical: 'top', horizontal: 'center' }}
						MenuListProps={{
							'aria-labelledby': 'product-button',
							onMouseEnter: clearProductCloseTimer,
							onMouseLeave: queueProductMenuClose
						}}
						PaperProps={{
							onMouseEnter: clearProductCloseTimer,
							onMouseLeave: queueProductMenuClose
						}}
					>
						<CustomMenuItem
							component={RouterLink}
							to={ASSESSMENT_BUSINESS_PATH}
							onClick={() => {
								trackCta('product page clicked', {
									cta_label: 'Assessment Service for Businesses',
									product: 'assessment',
									app_surface: 'business',
									location: 'nav'
								});
								setProductAnchorEl(null);
							}}
						>
							<Box sx={ddIconSx}>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={1.8}
									strokeLinecap="round"
									strokeLinejoin="round"
									width={19}
									height={19}
									aria-hidden="true"
								>
									<rect x="3" y="7" width="18" height="13" rx="2" />
									<path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
								</svg>
							</Box>
							<Box sx={ddTitleSx}>Assessment Service for Businesses</Box>
						</CustomMenuItem>
						<Divider sx={{ height: '1px', backgroundColor: '#EEF0F3', margin: '8px 6px', border: 'none' }} />
						<CustomMenuItem
							component={RouterLink}
							to={ASSESSMENT_CANDIDATE_PATH}
							onClick={() => {
								trackCta('product page clicked', {
									cta_label: 'Assessment Service for Candidates',
									product: 'assessment',
									app_surface: 'candidate',
									location: 'nav'
								});
								setProductAnchorEl(null);
							}}
						>
							<Box sx={ddIconSx}>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={1.8}
									strokeLinecap="round"
									strokeLinejoin="round"
									width={19}
									height={19}
									aria-hidden="true"
								>
									<path d="M20 21a8 8 0 0 0-16 0" />
									<circle cx="12" cy="7" r="4" />
								</svg>
							</Box>
							<Box sx={ddTitleSx}>Assessment Service for Candidates</Box>
						</CustomMenuItem>
						<Divider sx={{ height: '1px', backgroundColor: '#EEF0F3', margin: '8px 6px', border: 'none' }} />
						<CustomMenuItem
							component={RouterLink}
							to="/product/6d-assessment"
							onClick={() => {
								setProductAnchorEl(null);
							}}
						>
							<Box sx={ddIconSx}>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={1.8}
									strokeLinecap="round"
									strokeLinejoin="round"
									width={19}
									height={19}
									aria-hidden="true"
								>
									<path d="M22 10 12 5 2 10l10 5 10-5Z" />
									<path d="M6 12v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" />
								</svg>
							</Box>
							<Box sx={ddTitleSx}>MyCoach (L&amp;D)</Box>
						</CustomMenuItem>
					</CustomMenu>
					{/* Centers the nav */}
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							'@media (max-width: 1052px)': {
								justifyContent: 'flex-start'
							}
						}}
					>
						<StyledTabs
							ref={tabsRef}
							value={selectedTab}
							onChange={(_, newValue) => setSelectedTab(newValue)}
							textColor="primary"
							indicatorColor="primary"
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
						>
							<StyledTab
								label={
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<span>Product</span>
										<Box
											component="span"
											sx={{
												display: 'inline-flex',
												alignItems: 'center',
												cursor: 'pointer',
												padding: '6px',
												marginLeft: '2px'
											}}
											onClick={e => {
												e.stopPropagation();
												setProductAnchorEl(e.currentTarget);
											}}
											id="product-button"
											aria-haspopup="true"
											aria-controls={productAnchorEl ? 'product-menu' : undefined}
											aria-expanded={productAnchorEl ? true : false}
											aria-label="Open Product Menu"
										>
											<svg
												width="12"
												height="7"
												viewBox="0 0 12 7"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												role="img"
												aria-hidden="true"
												style={{
													transform: productAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
													transition: 'transform 0.16s ease'
												}}
											>
												<path
													fillRule="evenodd"
													clipRule="evenodd"
													d="M0.237976 6.26202C0.555277 6.57933 1.06972 6.57933 1.38702 6.26202L5.6875 1.96155L9.98798 6.26202C10.3053 6.57933 10.8197 6.57933 11.137 6.26202C11.4543 5.94472 11.4543 5.43028 11.137 5.11298L6.26202 0.237976C5.94472 -0.0793252 5.43028 -0.0793252 5.11298 0.237976L0.237976 5.11298C-0.0793253 5.43028 -0.0793253 5.94472 0.237976 6.26202Z"
													fill={
														pathname.startsWith('/product') || pathname.startsWith('/assessment-service') ? '#0073DE' : '#000'
													}
												/>
											</svg>
										</Box>
									</Box>
								}
								value={1}
								onMouseEnter={openProductMenu}
								onClick={openProductMenu}
							/>
							<StyledTab
								label="About Us"
								value={4}
								onMouseEnter={closeProductMenu}
								onClick={() => navigate('/about-us')}
							/>
							<StyledTab
								label="Trust Center"
								value={7}
								onMouseEnter={closeProductMenu}
								onClick={() => navigate('/trust-center')}
							/>
						</StyledTabs>
					</Box>
				</Toolbar>
			</AppBar>
		</Box>
	);
};

export default TopNavMenu;
